import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useAccessibilityTags } from "common/hooks/business/home/useAccessibilityTags";
import { useAccessibilityTagsHomePage } from "common/hooks/business/home/useAccessibilityTagsHomePage";
import { useSearchFilterSections } from "common/hooks/business/home/useSearchFilterSections";
import { useAuth, useSavedFilters, useFilters, useI18N } from "common/hooks";
import { useAtom } from "jotai";
import { savedFiltersAppliedAtom } from "@/store/atoms";
import Switch from "@/components/common/Switch";
import AccessibilityCollapse from "./AccessibilityCollapse";
import {
  buildTagSections,
  mergeTagSections,
  subtractSelections,
  unionSelections,
  withFamilySections,
} from "common/utils/accessibilitySelection";
import {
  searchPageSections,
  selectionOf,
  splitStepFreeAlternatives,
  withSelection,
  type FilterSectionLike,
  type FilterSectionTag,
} from "@/lib/accessibilityFilterState";

/**
 * Context-aware accessibility features component that works on both home and search pages.
 * This component automatically detects which page it's on and uses the appropriate data source:
 * - Search page: the search's own filter sections when the API sent them,
 *   else accessibilityTagsAtom (from homePinsAtom/venueSearchParamsAtom)
 * - Home page: Uses desktopHomePageAccessibilityTagsAtom (from desktopHomePagePinsAtomFamily)
 *
 * This fixes the issue where opening search bar immediately after page load
 * would show no accessibility filters.
 */
function AccessibilityFeaturesContextAware() {
  const i18n = useI18N();
  const { isLoggedIn } = useAuth();
  const { savedFilterKeys, loadSavedFilters } = useSavedFilters();
  const { setFiltersState } = useFilters();
  const [isSavedFiltersApplied, setIsSavedFiltersApplied] = useAtom(
    savedFiltersAppliedAtom
  );

  // Get data from both sources
  const searchPageData = useAccessibilityTags();
  const homePageData = useAccessibilityTagsHomePage();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const scopedSections = searchPageSections(pathname, useSearchFilterSections());
  const scoped = useMemo(
    () => (scopedSections !== undefined ? splitStepFreeAlternatives(scopedSections) : null),
    [scopedSections],
  );

  // Intelligently choose which data to use:
  // Prefer search page data if it has content, otherwise fall back to home page data
  // This ensures the component works on both pages without needing explicit context
  const fallback =
    searchPageData.accessibilityTags && searchPageData.accessibilityTags.length > 0
      ? searchPageData
      : homePageData;
  const accessibilityTags = scopedSections ?? fallback.accessibilityTags;
  const preparedData: FilterSectionLike[] = scoped ? scoped.preparedData : fallback.preparedData;
  const tagsToAdd: FilterSectionTag[] = scoped ? scoped.tagsToAdd : fallback.tagsToAdd;

  // A first tick spreads over the basic sections only; a section-less key materialises into every visible box.
  const tagSections = useMemo(
    () => buildTagSections(preparedData.filter((section) => section.kind !== "complex")),
    [preparedData],
  );
  const allSections = useMemo(() => buildTagSections(preparedData), [preparedData]);
  const complexSectionIds = useMemo(
    () => preparedData.filter((section) => section.kind === "complex").map((section) => section.id),
    [preparedData],
  );

  // Tell screen readers when a new search changes how many sections there are.
  const sectionCount = preparedData.filter((option) => option.accessibility_tags.length > 0).length;
  const announcedSectionCount = useRef<number | null>(null);
  const [liveMessage, setLiveMessage] = useState("");
  useEffect(() => {
    if (announcedSectionCount.current !== null && announcedSectionCount.current !== sectionCount) {
      setLiveMessage(i18n.t("accessibilitySectionsAvailable", { count: sectionCount }));
    }
    announcedSectionCount.current = sectionCount;
  }, [sectionCount, i18n]);

  // Load saved filters when component mounts
  useEffect(() => {
    if (isLoggedIn) {
      loadSavedFilters();
    }
  }, [isLoggedIn, loadSavedFilters]);

  // Apply saved filters function
  const applySavedFilters = useCallback(
    (apply: boolean) => {
      setIsSavedFiltersApplied(apply);

      if (savedFilterKeys) {
        // Section-aware: a legacy "any section" tick is made explicit before the union.
        setFiltersState((prev) => {
          const keys = selectionOf(prev);
          const visibleSections = mergeTagSections(
            allSections,
            withFamilySections(tagSections, keys, 428, tagsToAdd.map((tag) => tag.id)),
          );
          return withSelection(
            prev,
            apply
              ? unionSelections(keys, savedFilterKeys, visibleSections)
              : subtractSelections(keys, savedFilterKeys, visibleSections),
          );
        });
      }
    },
    [savedFilterKeys, allSections, tagSections, tagsToAdd, setFiltersState, setIsSavedFiltersApplied]
  );

  // Don't render if no accessibility tags data is available
  // Check for both null/undefined AND empty array
  if (!accessibilityTags || accessibilityTags.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {/* Saved Filters Toggle */}
      {isLoggedIn && (
        <div className="px-4 pt-2 pb-2">
          <div className="flex items-center gap-3 py-3 bg-bg-brand-ghost-default">
            <Switch
              isOn={isSavedFiltersApplied}
              onChange={(value) => {
                applySavedFilters(value);
              }}
              aria-label={
                isSavedFiltersApplied
                  ? `${i18n.get("applySavedAccessibilityFilters")} selected`
                  : `${i18n.get("applySavedAccessibilityFilters")}`
              }
            />
            <div className="text-primary-900 dark:text-primary-150 paragraph-medium-regular">
              {i18n.get("applySavedAccessibilityFilters")}
            </div>
          </div>
        </div>
      )}

      {/* Category Accordions */}
      {preparedData.map((option) => {
        if (option.accessibility_tags.length <= 0) return null;
        return (
          <AccessibilityCollapse
            label={option.name}
            key={option.id}
            sectionId={option.id}
            kind={option.kind}
            tagsToShow={option.accessibility_tags}
            tagSections={tagSections}
            allSections={allSections}
            tagsToAdd={tagsToAdd}
            complexSectionIds={complexSectionIds}
            headingLevel={2}
          />
        );
      })}

      {/* Visually hidden live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveMessage}
      </div>
    </div>
  );
}

export default AccessibilityFeaturesContextAware;
